import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import ThemeSelect from "@/ui/fields/Select";

import { Dialog, IconButton, TextField } from "@ui";
import { CloseIcon } from "@ui";
import type { Company } from "@/pages/private/dashboard/SharePage";
import DoubleToggle from "./DoubleToggle";
import { AddIcon } from "@ui";
import { ApplianceData } from "@/shared/api/types";
import { sharePageEndpoints } from "@/shared/api/endpoints";
import { httpGet, httpPost, httpPut } from "@/shared/api/http";
import femaCapture from "@/shared/fema/capture";
import { ThemeColours } from "@/clients/acme/config/colours";
import { openConfirmDialog } from "./ConfirmDialog";

interface CadentGasDialogProps {
  onClose: () => void;
  company: Company;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
}

interface Appliance {
  name: string;
  location: string;
  locationField: string;
  make: string;
  makeField: string;
  date: string;
  dateField: string;
  hasAppliance: boolean;
}

const CadentGasDialog: React.FC<CadentGasDialogProps> = ({
  onClose,
  company,
  setCompanies,
}) => {
  const [appliancesWorking, setAppliancesWorking] = useState<boolean | null>(
    null
  );
  const [hasDataAlready, setHasDataAlready] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const [applianceData, setApplianceData] = useState<ApplianceData>({
    creationDate: "",
    lastUpdateDate: "",
    createdBy: 0,
    lastUpdatedBy: 0,
    id: 0,
    areAllGasAppliancesWorking: "",
    locationGasCooker: "",
    makeOrModelGasCooker: "",
    dateOfServiceGasCooker: "",
    locationGasHob: "",
    makeOrModelGasHob: "",
    dateOfServiceGasHob: "",
    locationGasFire: "",
    makeOrModelGasFire: "",
    dateOfServiceGasFire: "",
    locationBoller: "",
    makeOrModelBoller: "",
    dateOfServiceBoller: "",
    companyName: "",
    accountNumber: "",
    customerId: 0,
    sharingId: 0,
    isResponsibleForMaintenance: "",
    gasCookerOrderKey: "",
    gasHobOrderKey: "",
    gasFireOrderKey: "",
    bollerOrderKey: "",
  });

  const [appliances, setAppliances] = useState<Appliance[]>([
    {
      name: "Gas Cooker",
      hasAppliance: false,
      location: "",
      locationField: "locationGasCooker",
      make: "",
      makeField: "makeOrModelGasCooker",
      date: "",
      dateField: "dateOfServiceGasCooker",
    },
    {
      name: "Gas Hob",
      hasAppliance: false,
      location: "",
      locationField: "locationGasHob",
      make: "",
      makeField: "makeOrModelGasHob",
      date: "",
      dateField: "dateOfServiceGasHob",
    },
    {
      name: "Gas Fire",
      hasAppliance: false,
      location: "",
      locationField: "locationGasFire",
      make: "",
      makeField: "makeOrModelGasFire",
      date: "",
      dateField: "dateOfServiceGasFire",
    },
    {
      name: "Boiler",
      hasAppliance: false,
      location: "",
      locationField: "locationBoller",
      make: "",
      makeField: "makeOrModelBoller",
      date: "",
      dateField: "dateOfServiceBoller",
    },
  ]);

  // GET Cadent Gas Data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    async function getCadentGasData() {
      try {
        const response = await httpGet<ApplianceData[]>(
          sharePageEndpoints.getCadentAdditionalQuestions(
            customerData.customerId
          )
        );

        const res: any = response?.data;

        const data = res.find((entry: ApplianceData) => {
          return company.id == entry.sharingId;
        });

        if (data && company.id) {
          for (const appliance of appliances) {
            appliance.location = data[appliance.locationField];
            appliance.make = data[appliance.makeField];
            appliance.date = data[appliance.dateField];

            if (appliance.location || appliance.make || appliance.date) {
              appliance.hasAppliance = true;
            }
          }

          setApplianceData(data);
          setAppliancesWorking(data.areAllGasAppliancesWorking == "Y");
          setHasDataAlready(true);
        }
      } catch (error) {
        femaCapture("fetching_cadent_gas_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getCadentGasData();
  }, []);

  async function updateApplianceDate(e, idx: number) {
    const value = e.target.value;

    if (!value) {
      setAppliances((prev) =>
        prev.map((item, i) => (i === idx ? { ...item, date: "" } : item))
      );
      return;
    }

    const selectedDate = new Date(value);

    if (isNaN(selectedDate.getTime())) return;

    const today = new Date();

    if (selectedDate > today) return;

    setAppliances((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...item, date: selectedDate.toISOString() } : item
      )
    );
  }

  async function removeAppliance(idx: number) {
    const yes = await openConfirmDialog({
      title: "Remove Appliance",
      message: "Are you sure you want to remove this appliance?",
      confirmText: "Yes, Remove",
      cancelText: "Cancel",
    });

    if (!yes) {
      return;
    }

    setAppliances((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...item, hasAppliance: false } : item
      )
    );
  }

  const locations: string[] = [
    "Lounge",
    "Dining Room",
    "Kitchen",
    "Bedroom",
    "Bathroom",
    "Loft",
    "Garage",
  ];

  async function handleSave() {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    const payload: Record<string, string> = {
      ...JSON.parse(JSON.stringify(applianceData)),
      customerId: customerData.customerId,
      sharingId: company.id,
    };

    payload.areAllGasAppliancesWorking = appliancesWorking ? "Y" : "N";

    for (const appliance of appliances) {
      if (appliance.hasAppliance) {
        payload[appliance.locationField] = appliance.location;
        payload[appliance.makeField] = appliance.make;
        payload[appliance.dateField] =
          appliance.date || new Date().toISOString().split("T")[0];
      } else {
        payload[appliance.locationField] = "";
        payload[appliance.makeField] = "";
        payload[appliance.dateField] = "";
      }
    }

    if (!hasDataAlready) {
      delete payload.id;
      try {
        await httpPost<ApplianceData>(
          sharePageEndpoints.createCadentAdditionalQuestions(),
          payload
        );
      } catch (e) {
        femaCapture("create_cadent_entry", e, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    } else {
      try {
        await httpPut<ApplianceData>(
          sharePageEndpoints.createCadentAdditionalQuestions(),
          payload
        );
      } catch (e) {
        femaCapture("update_cadent_entry", e, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    }

    const fieldsToCheck = [
      "locationBoller",
      "locationGasCooker",
      "locationGasFire",
      "locationGasHob",
    ];

    let applianceCount = 0;

    for (const field of fieldsToCheck) {
      if (payload[field]) {
        applianceCount += 1;
      }
    }

    setCompanies((prev) => {
      prev.map((c) => {
        if (c.id === company.id) {
          c.applianceCount = applianceCount;
        }

        return c;
      });

      return prev;
    });

    setOpenConfirmation(true);
  }

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Dialog Title */}
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e0e0e0",
            px: 4,
            justifyContent: "space-between",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Gas Appliances
          </Typography>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent sx={{ pt: 3, px: 4, flexGrow: 1, overflowY: "auto" }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 500, my: 1, fontSize: 18 }}
          >
            Are all your gas appliances working?
          </Typography>
          <DoubleToggle
            value={appliancesWorking}
            onChange={(val) => {
              setAppliancesWorking(val);
            }}
            selectedColor="#165F54"
            nonSelectedColor="#d5d5d569"
            textColor="#FFFFFF"
            sx={{ mb: 2 }}
          />

          {appliances.map((appliance, idx) => (
            <Box sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                {/* Appliance Name */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 500,
                    mb: 0,
                    color: "#00717A",
                    fontSize: 16,
                  }}
                >
                  {appliance.name}
                </Typography>

                {appliance.hasAppliance && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "red",
                      fontWeight: 500,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() => {
                      removeAppliance(idx);
                    }}
                  >
                    Remove
                  </Typography>
                )}
              </Box>

              {!appliance.hasAppliance && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    px: 1.5,
                    backgroundColor: "#fff",
                    py: 1,
                    border: "1.5px solid #165F54",
                    borderRadius: 1,
                    mb: 0,
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={() => {
                    setAppliances((prev) =>
                      prev.map((item, i) =>
                        i === idx ? { ...item, hasAppliance: true } : item
                      )
                    );
                  }}
                >
                  <AddIcon sx={{ fontSize: 20, color: "#165F54" }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 500,
                      mb: 0,
                      color: "#165F54",
                      fontSize: 16,
                    }}
                  >
                    Add {appliance.name}
                  </Typography>
                </Box>
              )}

              {appliance.hasAppliance && (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 500,
                      mb: 0,
                      color: "#00717A",
                      fontSize: 15,
                      mt: 1,
                    }}
                  >
                    Select Location
                  </Typography>

                  <ThemeSelect
                    dialogLabel="Select a Location"
                    options={[
                      ...locations.map((location) => ({
                        value: location,
                        label: location,
                      })),
                    ]}
                    value={appliance.location}
                    onChange={(value) => {
                      setAppliances((prev) =>
                        prev.map((item, i) =>
                          i === idx
                            ? { ...item, location: value as string }
                            : item
                        )
                      );
                    }}
                    placeholder="Select a Location"
                    showErrorBelow={false}
                    searchable={true}
                    fullWidth
                  />

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 500,
                      mb: 0,
                      color: "#00717A",
                      fontSize: 15,
                      mt: 2,
                    }}
                  >
                    Make
                  </Typography>

                  <TextField
                    value={appliance.make || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAppliances((prev) =>
                        prev.map((item, i) =>
                          i === idx ? { ...item, make: value } : item
                        )
                      );
                    }}
                    fullWidth
                  />

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 500,
                      mb: 0,
                      color: "#00717A",
                      fontSize: 15,
                    }}
                  >
                    Date
                  </Typography>

                  <TextField
                    type="date"
                    value={appliance.date ? appliance.date.split("T")[0] : ""}
                    onChange={(e) => {
                      updateApplianceDate(e, idx);
                    }}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: new Date().toISOString().split("T")[0],
                    }}
                  />
                </>
              )}
            </Box>
          ))}
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            zIndex: 1,
            boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: "none" }}
            size="large"
            onClick={() => {
              handleSave();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Box>

      {/* Confirmation Popup */}
      <Dialog
        open={openConfirmation}
        onClose={() => {
          setOpenConfirmation(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "15.025px",
            boxShadow: "0px 15.025px 45.074px 0px rgba(0,0,0,0.15)",
            px: "22px",
            py: 0,
            m: 2,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: "18px",
                borderBottom: "1.502px solid rgba(68, 72, 109, 0.1)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#000",
                  lineHeight: "20.952px",
                }}
              >
                Confirm Appliances
              </Typography>
              <IconButton
                onClick={() => {
                  setOpenConfirmation(false);
                }}
                sx={{
                  backgroundColor: "#e5e7eb",
                  borderRadius: "32px",
                  padding: "8px",
                  width: "40px",
                  height: "40px",
                  "&:hover": {
                    backgroundColor: "#d1d5db",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: "24px", color: "#000" }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pt: "16.152px",
                pb: "24px",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 400, color: ThemeColours.subtitleVar1 }}
              >
                Have you added all your gas appliances?
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: "22.537px",
                  width: "100%",
                }}
              >
                <Button
                  onClick={() => {
                    setOpenConfirmation(false);
                  }}
                  sx={{
                    flex: 1,
                    height: "45.074px",
                    borderRadius: "10px",
                    color: "#1f2937",
                    fontSize: "18px",
                    fontWeight: 600,
                    textTransform: "none",
                    backgroundColor: "#f3f4f6",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "#e5e7eb",
                      border: "none",
                    },
                    "&:disabled": {
                      backgroundColor: "#f3f4f6",
                      color: "#9ca3af",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setOpenConfirmation(false);

                    setTimeout(() => {
                      onClose();
                    }, 100);
                  }}
                  sx={{
                    flex: 1,
                    height: "45.074px",
                    backgroundColor: "#D4E03B",
                    borderRadius: "10px",
                    color: "#003838",
                    fontSize: "18px",
                    fontWeight: 600,
                    textTransform: "none",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "#e6f332ff",
                      border: "none",
                    },
                    "&:disabled": {
                      backgroundColor: "#d5e03b64",
                      color: "#0038384f",
                    },
                  }}
                >
                  Yes, Save
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CadentGasDialog;
